/**
 * A sub-dialog of the subscription wizard: warns which features a plan change will lose.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogClose,
  Button,
} from '@scio3/ui';
import { getPlanWarningIcon } from './plan-warning-icons';

/**
 * The features worth showing FIRST, most-missed downward.
 *
 * `validateChangePlan` returns lost features in the `productPlans` document's own order, which is
 * storage order — so a Pro Plus → Standard downgrade opened with whatever happened to be first and
 * buried SAML and Teams somewhere in row nine of thirty-three. Ranking them puts the ones that
 * actually change how a customer works at the top, where the truncated view can only fit a few.
 *
 * Strings are the BACKEND's wording, matching `plan-warning-icons.ts` (which documents why that
 * vocabulary differs from our card bullets). A label not listed here is not dropped — it sorts
 * after the ranked ones in its original order, so a renamed or brand-new plan feature degrades to
 * "unranked" rather than disappearing.
 */
const FEATURE_PRIORITY = [
  'Single Sign On (SAML)',
  'Teams',
  'Role Security',
  'Folder Level Security',
  'Proof Of Play',
  'Dynamic Data Mapping',
  'Advanced Device Management',
  'Approval Workflow',
  'Custom Domain & Branding',
  'Monitoring & Alert',
  'Api Key',
  'Remote Control',
  'Security Logging',
  'Advanced Schedule',
] as const;

/** How many rows survive the collapse. Eight = four rows of the two-column grid. */
const COLLAPSED_COUNT = 8;

/** Ranked features first, everything else after in the order the backend sent it. */
function byPriority(items: string[]): string[] {
  const rank = (text: string) => {
    const i = FEATURE_PRIORITY.indexOf(text as (typeof FEATURE_PRIORITY)[number]);
    return i === -1 ? FEATURE_PRIORITY.length : i;
  };
  return items
    .map((text, index) => ({ text, index }))
    .sort((a, b) => rank(a.text) - rank(b.text) || a.index - b.index)
    .map((entry) => entry.text);
}

type ChangePlanWarning = {
  messageDisplay?: string;
  textValues?: string[];
  requiredRemove?: boolean;
  url?: string;
  plan?: string;
};

type Props = {
  open: boolean;
  warnings: ChangePlanWarning[];
  onDismiss: () => void;
  onConfirm: () => void;
  /** Custom label for the confirm button (default: 'Next') */
  confirmLabel?: string;
  /** Column headings for the comparison table. Without both, it falls back to the flat list. */
  currentPlanName?: string;
  newPlanName?: string;
};

/**
 * Plan change warning — what the chosen plan will take away, before it is charged for.
 *
 * Ported from scio1's ConfirmDowngradeSubscriptionModal, but NOT pixel-for-pixel: scio1 painted a
 * full-bleed orange header bar with white text and listed every lost feature as a `- item` line.
 * With a Pro Plus → Standard downgrade that is 30+ rows, so the dialog became a tall column of
 * dashes under a colour that appears nowhere else in the product. This version keeps the same
 * information and the same decision, in the shape the rest of our dialogs use:
 *
 *   - header, comparison card and footer all match `dialogs/DowngradeComparisonDialog`, the
 *     sibling that handles the downgrade-to-Free case. Two dialogs asking the same question of the
 *     same customer should not look like two different products, so that one is the reference for
 *     layout here (and the standard close button replaces scio1's hand-rolled white one);
 *   - the lost features as a before/after comparison, ranked and capped, with the full flat list
 *     one click behind "+N more" — the long list is still reachable, it is just no longer the
 *     first thing you meet;
 *   - the backend's `messageDisplay` promoted to the header as the subtitle when there is only one
 *     warning, so the body is purely the list. A downgrade overrides it with our own copy.
 *
 * Deliberate divergence from scio1: it hid Cancel whenever OK was shown (`showOK` implied
 * `showCancel = false`), leaving the X as the only way to back out of a subscription change. Both
 * are shown here — declining a downgrade deserves a real target, and our dialog convention puts a
 * Cancel before every action button. The `requiredRemove` case still shows Close alone, since
 * there is no "proceed" to offer.
 *
 * Backend `validateChangePlan` supplies the warnings: `messageDisplay`, `textValues`,
 * `requiredRemove`, an optional `url` ("Go To"), and `plan` (interpolated into messageDisplay).
 */
export function ChangePlanWarningDialog({
  open,
  warnings,
  onDismiss,
  onConfirm,
  confirmLabel,
  currentPlanName,
  newPlanName,
}: Props) {
  const { t } = useTranslation();
  /** Per-warning, because a validation run can return several lists. */
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});

  const hasRequiredRemove = warnings.some((w) => w.requiredRemove);
  // Nothing to proceed to when the user must go and remove something first.
  const canProceed = !hasRequiredRemove;
  /**
   * Whether this is actually a downgrade, which decides the confirm label.
   *
   * `validateChangePlan` runs on ANY plan change, upgrades included, and one of its warnings is
   * about team billing rather than lost features — so a Standard → Pro Plus upgrade can land here
   * with nothing being taken away. Only the feature-loss warnings carry `textValues`, so their
   * presence is what makes "downgrade" a true word to put on the button.
   */
  const isDowngrade = warnings.some((w) => (w.textValues?.length ?? 0) > 0);
  /** With one warning its message reads as the dialog's subtitle; with several each keeps its own. */
  const isSingle = warnings.length === 1;
  /**
   * A downgrade gets our own question-and-consequence copy; anything else keeps the backend's.
   *
   * The backend `messageDisplay` for this case is "The following features are not supported with
   * your chosen plan:" — accurate, but it labels a list rather than asking for a decision, and this
   * dialog exists to get one. Non-downgrade warnings (the team-billing one, `requiredRemove`) carry
   * information our copy does not know, so they keep theirs untouched.
   */
  const subtitle = isDowngrade
    ? t("If you downgrade now, here's what will change")
    : isSingle
      ? t(warnings[0]?.messageDisplay ?? '', { plan: warnings[0]?.plan ?? '' })
      : null;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onDismiss();
      }}
    >
      <DialogContent
        className="flex max-h-[85vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-xl"
        data-testid="change-plan-warning-dialog"
      >
        {/* Header copied from `dialogs/DowngradeComparisonDialog` so the two downgrade dialogs
            open identically: plain card surface, no muted strip, no warning icon, title then a
            muted line under it. `pr-8` clears the close button.
            Dropped from the previous version: the `bg-muted/50` strip and the `text-warning`
            AlertTriangle. Both were carried over from our neutral detail dialogs (ExpiredItemsDialog
            et al), but the sibling this one actually pairs with has neither, and a tinted bar plus a
            caution glyph above a table that is already all red crosses was saying "warning" three
            times. */}
        <div className="shrink-0 px-6 pr-8 pt-6">
          <DialogTitle className="text-xl font-semibold">
            {isDowngrade ? t('Are you sure you want to downgrade?') : t('Plan Change Warning')}
          </DialogTitle>
          {subtitle && (
            <DialogDescription className="mt-1 text-sm text-muted-foreground">
              {subtitle}
            </DialogDescription>
          )}
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-6 pb-2 pt-5">
          {warnings.map((warning, idx) => {
            /* Ranked, then truncated. Thirty-three rows read as a wall and made a real
               trade-off feel like fine print; eight ranked ones say the same thing in a glance.
               NOT permanently hidden, though — this dialog is the consent step for losing these
               capabilities, so the rest stay one click away rather than gone. */
            const items = byPriority(warning.textValues ?? []);
            const isExpanded = !!expanded[idx];
            const visible = isExpanded ? items : items.slice(0, COLLAPSED_COUNT);
            const hiddenCount = items.length - visible.length;
            return (
              <div key={idx} className="space-y-2">
                {/* Already in the header when it is the only warning — see `subtitle`. */}
                {!isSingle && !isDowngrade && (
                  <div className="flex items-start justify-between gap-3">
                    <p className="flex-1 text-sm font-semibold text-foreground">
                      {t(warning.messageDisplay ?? '', { plan: warning.plan ?? '' })}
                    </p>
                  </div>
                )}
                {/* COLLAPSED — the comparison layout from `dialogs/DowngradeComparisonDialog`,
                    copied rather than reinvented so the two downgrade dialogs are the same object
                    to a customer. Same grid (`1fr_88px_88px`), same circled row icons, same
                    absolutely-positioned strip behind the right column — that strip is the trick:
                    it spans the card's full height through its own padding, which is what makes
                    the tinted column continuous instead of a stack of per-row cells.
                    The one difference is the row data. That dialog hardcodes seven Free-plan rows
                    with per-cell `check | x | dash | text`; here the rows are backend-driven
                    (`textValues` is the lost-feature list), so every row is check → x by
                    construction. Needs both plan names for the headers; without them it falls
                    through to the flat list below. */}
                {items.length > 0 && !isExpanded && currentPlanName && newPlanName && (
                  <div className="relative overflow-hidden rounded-xl border border-border bg-card pb-4 pt-2">
                    <div
                      aria-hidden
                      className="pointer-events-none absolute inset-y-0 right-0 w-[88px] bg-muted"
                    />
                    <div className="relative">
                      <div className="grid grid-cols-[1fr_88px_88px] items-stretch gap-x-1 pl-8 text-sm font-semibold">
                        <span />
                        <span className="py-2 pr-1 text-center">{currentPlanName}</span>
                        <span className="flex items-center justify-center px-2 py-2">
                          {newPlanName}
                        </span>
                      </div>
                      {visible.map((text, i) => {
                        // Per-feature glyph rather than the same mark 33 times: at this length an
                        // identical row marker is just texture, while a distinct icon lets someone
                        // scan for the one capability they actually care about.
                        const Icon = getPlanWarningIcon(text);
                        return (
                          <div
                            key={i}
                            className="grid grid-cols-[1fr_88px_88px] items-stretch gap-x-1 pl-8 text-sm"
                          >
                            <div className="flex items-center gap-2 py-2 pr-2">
                              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
                                <Icon aria-hidden="true" className="h-4 w-4 text-foreground" />
                              </span>
                              <span className="text-foreground">{t(text)}</span>
                            </div>
                            <div className="flex items-center justify-center py-2 pr-1">
                              <Check className="h-4 w-4 text-primary" aria-label={t('Included')} />
                            </div>
                            <div className="flex items-center justify-center px-2 py-2">
                              <X
                                className="h-4 w-4 text-destructive"
                                aria-label={`${t(text)} — ${t('Not included')}`}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {/* EXPANDED (and the fallback when plan names are missing) — the plain list. Once
                    every row is on screen the per-row plan columns are pure repetition, so the
                    long view drops back to names in two columns. */}
                {items.length > 0 && (isExpanded || !currentPlanName || !newPlanName) && (
                  // No card around it: this list IS the body of the dialog, so a border would be
                  // drawing a box inside a box. The two-column grid is enough structure.
                  <ul className="grid gap-x-6 gap-y-2.5 sm:grid-cols-2">
                    {items.map((text, i) => {
                      const Icon = getPlanWarningIcon(text);
                      return (
                        <li key={i} className="flex items-start gap-2 text-sm text-foreground">
                          <Icon
                            className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
                            aria-hidden="true"
                          />
                          <span>{t(text)}</span>
                        </li>
                      );
                    })}
                  </ul>
                )}
                {/* Sits under the grid rather than as a final list item: it is a control, not one
                    of the features being lost, and a fake row inside a two-column grid lands in
                    whichever column happens to be next. */}
                {(hiddenCount > 0 || isExpanded) && (
                  <button
                    type="button"
                    onClick={() => setExpanded((prev) => ({ ...prev, [idx]: !isExpanded }))}
                    className="cursor-pointer text-sm font-medium text-foreground underline transition-colors hover:text-primary"
                    data-testid="change-plan-warning-more-btn"
                  >
                    {isExpanded
                      ? t('Show less')
                      : hiddenCount > 0
                        ? t('+{{count}} more', { count: hiddenCount })
                        : t('Show all differences')}
                  </button>
                )}
                {/* scio1 renders "Go To" only when a warning has BOTH a url and requiredRemove —
                    it is the route to whatever must be removed first, so it belongs with its own
                    warning rather than in the footer with the dialog-level actions. */}
                {warning.requiredRemove && warning.url && (
                  <a
                    href={warning.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex"
                  >
                    <Button variant="outline">{t('Go To')}</Button>
                  </a>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-end gap-3 bg-card px-6 py-4">
          <DialogClose asChild>
            <Button variant="ghost" data-testid="change-plan-warning-dismiss-btn">
              {canProceed ? t('Cancel') : t('Close')}
            </Button>
          </DialogClose>
          {canProceed && (
            <Button data-testid="change-plan-warning-confirm-btn" onClick={onConfirm}>
              {/* Names the consequence instead of the direction. "Next" made losing 33 features
                  sound like paging through a form, and this is the last screen before the change
                  is committed. */}
              {confirmLabel ?? (isDowngrade ? t('Continue with downgrade') : t('Continue'))}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
